import React, { useState } from "react";
import { ChevronDown, ChevronRight, ShieldCheck } from "lucide-react";
import { Tooltip } from "@mui/material";
import StatusChip from "../common/StatusChip";
import VideoOnMetaChip from "../common/VideoOnMetaChip";
import { Button } from "../ui/button";
import { formatDisplayDateTime } from "../../utils/helperFunctions";
import { deliveryModerationChipStatus } from "../../utils/deliveryModeration";
import { IssueChips } from "../AdModeration/ModerationIssues";
import OverrideModerationModal from "./Modals/OverrideModerationModal";
import usePermissions from "../../hooks/usePermissions";
import useZustandStore from "../../zustandStore/useZustandStore";
import { PERMISSION_CODES } from "../../utils/constants/permissions";
import { isAgencyScopedUser } from "../../utils/agencyScope";

/** Free-text moderator feedback, shown as prose: it runs to whole paragraphs. */
const FeedbackList = ({ label, values, tone = "gray" }) =>
  Array.isArray(values) && values.length > 0 ? (
    <div>
      <p
        className={`mb-1 text-[11px] font-semibold uppercase tracking-wider ${
          tone === "rose" ? "text-rose-500" : "text-gray-400"
        }`}
      >
        {label}
      </p>
      <ul className="space-y-1.5">
        {values.map((v, i) => (
          <li
            key={i}
            className={`border-l-2 pl-3 text-sm leading-relaxed ${
              tone === "rose"
                ? "border-rose-300 text-gray-700"
                : "border-gray-200 text-gray-600"
            }`}
          >
            {typeof v === "string" ? v : JSON.stringify(v)}
          </li>
        ))}
      </ul>
    </div>
  ) : null;

const ACTION_LABEL = {
  delivered: { text: "Delivered", tone: "gray" },
  resubmitted: { text: "Resubmitted", tone: "blue" },
  updated: { text: "Feedback updated", tone: "gray" },
  comment: { text: "Comment", tone: "gray" },
  rejected: { text: "Rejected by moderator", tone: "rose" },
  approved: { text: "Approved by moderator", tone: "green" },
  "final rejected": { text: "Final rejected", tone: "rose" },
  "final approved": { text: "Final approved", tone: "green" },
  finalized: { text: "Finalized", tone: "gray" },
  requester_override: { text: "Overridden by requester", tone: "green" },
  super_admin_override: { text: "Overridden by Super Admin", tone: "green" },
};

const DOT_TONE = {
  rose: "bg-rose-400",
  green: "bg-emerald-400",
  blue: "bg-blue-400",
  gray: "bg-gray-300",
};

const TEXT_TONE = {
  rose: "text-rose-700",
  green: "text-emerald-700",
  blue: "text-blue-700",
  gray: "text-gray-700",
};

/** One event in a hook's moderation history. */
const HistoryEvent = ({ entry, last }) => {
  const meta = ACTION_LABEL[entry.action] ?? {
    text: entry.action || "Updated",
    tone: "gray",
  };
  return (
    <li className="relative flex gap-3 pb-3 last:pb-0">
      {!last && (
        <span className="absolute left-[3px] top-3 h-full w-px bg-gray-200" />
      )}
      <span
        className={`mt-1.5 h-[7px] w-[7px] shrink-0 rounded-full ${DOT_TONE[meta.tone]}`}
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className={`text-sm font-medium ${TEXT_TONE[meta.tone]}`}>
            {meta.text}
          </span>
          {entry.iteration > 1 && (
            <span className="text-[11px] text-gray-400">
              iteration {entry.iteration}
            </span>
          )}
          <span className="ml-auto text-[11px] text-gray-400">
            {entry.performed_by_name || "System"}
            {entry.created_on
              ? ` · ${formatDisplayDateTime(entry.created_on)}`
              : ""}
          </span>
        </div>
        {entry.comments && (
          <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-gray-600">
            {entry.comments}
          </p>
        )}
      </div>
    </li>
  );
};

/** "Override rejection" for an Editing-only rejection (super admin: any); disabled with the reason otherwise. */
const OverrideAction = ({ round, hookLabel, superAdmin }) => {
  const [open, setOpen] = useState(false);
  const enabled = round.overridable || superAdmin;
  const button = (
    <span>
      <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!enabled} onClick={() => setOpen(true)}>
        Override rejection
      </Button>
    </span>
  );
  return (
    <>
      {enabled ? button : <Tooltip arrow title="Only Editing Issue rejections can be overridden">{button}</Tooltip>}
      {open && <OverrideModerationModal open onClose={() => setOpen(false)} moderation={round} hookLabel={hookLabel} />}
    </>
  );
};

/** A hook's current state only: resubmission resets these fields, so the trail is in the history. */
const ModerationRound = ({ round, canOverride, superAdmin, hookLabel }) => {
  const [historyOpen, setHistoryOpen] = useState(false);
  const chip = deliveryModerationChipStatus({
    moderation_status: round.status,
    finalized_status: round.finalized_status,
  });
  const rejected = chip === "final_rejected" || chip === "moderation_rejected";
  const when = round.finalized_at || round.updated_on;
  const hasFeedback = Boolean(round.errors?.length || round.notes?.length || round.issues_highlighted?.length);
  const override = round.override;
  const history = Array.isArray(round.history) ? round.history : [];

  return (
    <div
      className={`rounded-lg border px-4 py-3 ${
        rejected ? "border-rose-200 bg-rose-50/30" : "border-gray-200 bg-white"
      }`}
    >
      <div className="mb-2.5 flex flex-wrap items-center gap-x-2 gap-y-1">
        <StatusChip subtle status={chip} size="small" />
        {round.video_on_meta && <VideoOnMetaChip />}
        {override && (
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
            <ShieldCheck className="h-3 w-3" />
            Overridden by {override.by || "requester"}
          </span>
        )}
        {rejected && canOverride && <OverrideAction round={round} hookLabel={hookLabel} superAdmin={superAdmin} />}
        <span className="ml-auto text-xs text-gray-400">
          {round.last_moderated_by_name || "Unassigned"}
          {when ? ` · ${formatDisplayDateTime(when)}` : ""}
        </span>
      </div>

      {hasFeedback && (
        <div className="mb-3 space-y-3">
          <FeedbackList label="Errors" values={round.errors} tone="rose" />
          <FeedbackList label="Notes" values={round.notes} />
          {round.issues_highlighted?.length > 0 && (
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Issues</span>
              <IssueChips codes={round.issues_highlighted} />
            </div>
          )}
        </div>
      )}

      {override && (
        <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50/50 px-3 py-2 text-sm text-gray-700">
          <p className="whitespace-pre-wrap">{override.reason}</p>
          <p className="mt-1 text-xs text-gray-400">{override.at ? formatDisplayDateTime(override.at) : ""}</p>
        </div>
      )}

      {history.length > 0 ? (
        <div className={hasFeedback ? "border-t border-gray-200/70 pt-3" : ""}>
          <button
            type="button"
            onClick={() => setHistoryOpen((v) => !v)}
            aria-expanded={historyOpen}
            className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400 transition hover:text-gray-600"
          >
            {historyOpen ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
            History
            <span className="font-normal normal-case tracking-normal">
              ({history.length} {history.length === 1 ? "event" : "events"})
            </span>
          </button>
          {historyOpen && (
            <ul className="mt-2.5">
              {history.map((h, i) => (
                <HistoryEvent
                  key={h.id}
                  entry={h}
                  last={i === history.length - 1}
                />
              ))}
            </ul>
          )}
        </div>
      ) : (
        !hasFeedback && (
          <p className="text-xs italic text-gray-300">No activity recorded</p>
        )
      )}
    </div>
  );
};

/** Moderation grouped by hook. Expects deliveries that already carry a non-empty `moderations`. */
export default function ModerationRounds({ deliveries }) {
  const { hasPermission, isSuperAdmin } = usePermissions();
  const currentUser = useZustandStore((st) => st?.currentUser);
  // Same rule as the API: ad_request.create, never an agency user; super admin always.
  const canOverride = isSuperAdmin || (hasPermission(PERMISSION_CODES.AD_REQUEST_CREATE) && !isAgencyScopedUser(currentUser));
  // Only a hook's newest attempt can be overridden; older ones were superseded by a resubmission.
  const latestIteration = {};
  deliveries.forEach((d) => {
    latestIteration[d.hook_number] = Math.max(latestIteration[d.hook_number] ?? 0, d.iteration ?? 1);
  });
  return (
    <div className="space-y-4">
      {deliveries.map((d) => (
        <div key={d.id}>
          <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
            <span className="text-sm font-semibold text-gray-800">
              Hook {d.hook_number ?? "—"}
            </span>
            <span
              className="w-full truncate font-mono text-[11px] text-gray-400"
              title={d.hook_name || d.ad_name || ""}
            >
              {d.hook_name || d.ad_name || "—"}
            </span>
          </div>
          <div className="space-y-2">
            {d.moderations.map((m) => (
              <ModerationRound
                key={m.id}
                round={m}
                hookLabel={`Hook ${d.hook_number ?? "—"}`}
                canOverride={canOverride && (d.iteration ?? 1) === latestIteration[d.hook_number]}
                superAdmin={isSuperAdmin}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
